import {
  Box,
  FormControlLabel,
  Grid,
  Pagination,
  Stack,
  Switch,
  Typography
} from '@mui/material';
import type { ApexOptions } from 'apexcharts';
import i18n from 'app/i18n';
import MainCard from 'components/MainCard';
import ProviderChip from 'components/ProviderChip';
import { apiSlice } from 'features/api/apiSlice';
import { getValue, normalizeCursorPage, toNumber } from 'helpers/PatientHealthDataHelper';
import PatientMetricCard from 'pages/patients/tabs/PatientMetricCard';
import PatientDateRangeSelector from 'pages/patients/tabs/PatientDateRangeSelector';
import type { PatientDateRange } from 'pages/patients/tabs/PatientDateRangeSelector';
import { BedDouble, Clock3, Moon, TrendingUp } from 'lucide-react';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import ApexChart from 'react-apexcharts';
import { useTranslation } from 'react-i18next';
import { store } from 'store';

export type SleepDayRange = PatientDateRange;

type SleepRecord = Record<string, unknown>;

type SleepSummaryTotals = {
  nightsTracked: number;
  totalSleepMinutes: number;
  avgEfficiency: number;
  avgDurationMinutes: number;
  avgBedtimeMinutes: number | null;
  avgBedtime: string;
  deepMinutes: number;
  remMinutes: number;
  lightMinutes: number;
  awakeMinutes: number;
};

type SleepMetricKey = 'avgEfficiency' | 'avgDuration' | 'avgBedtime';

interface SleepTabProps {
  userId: string;
}

function formatSleepAxisValue(value: number, metric: SleepMetricKey): string {
  switch (metric) {
    case 'avgBedtime':
      return formatBedtimeMinutes(value);
    case 'avgDuration':
      return formatDurationMinutes(value);
    default:
      return `${Math.round(value)}%`;
  }
}

function formatSleepTooltipValue(value: number, metric: SleepMetricKey): string {
  switch (metric) {
    case 'avgBedtime':
      return formatBedtimeMinutes(value);
    case 'avgDuration':
      return formatDurationMinutes(value);
    default:
      return `${Math.round(value)}%`;
  }
}

export function getSleepDateRange(range: SleepDayRange): { start: moment.Moment; end: moment.Moment } {
  return {
    start: moment().subtract(range, 'days').startOf('day'),
    end: moment().endOf('day')
  };
}

function getSessionStageBreakdown(record: SleepRecord) {
  const stages = (getValue(record, ['stages']) as Record<string, unknown> | undefined) ?? {};
  const deep = toNumber(getValue(stages, ['deep_minutes'])) ?? toNumber(getValue(record, ['deep_minutes'])) ?? 0;
  const rem = toNumber(getValue(stages, ['rem_minutes'])) ?? toNumber(getValue(record, ['rem_minutes'])) ?? 0;
  const light = toNumber(getValue(stages, ['light_minutes'])) ?? toNumber(getValue(record, ['light_minutes'])) ?? 0;
  const awake = toNumber(getValue(stages, ['awake_minutes'])) ?? toNumber(getValue(record, ['awake_minutes'])) ?? 0;

  const total = deep + rem + light + awake || 1;

  return [
    { key: 'deep', label: 'Deep', minutes: deep, color: '#6366f1', percent: (deep / total) * 100 },
    { key: 'rem', label: 'REM', minutes: rem, color: '#e45cf6', percent: (rem / total) * 100 },
    { key: 'light', label: 'Light', minutes: light, color: '#38bdf8', percent: (light / total) * 100 },
    { key: 'awake', label: 'Awake', minutes: awake, color: 'grey.300', percent: (awake / total) * 100 }
  ];
}

function getRecordSource(record: SleepRecord): string | null {
  const source = getValue(record, ['source']);
  if (!source) return null;
  if (typeof source === 'string' && source.trim()) return source.trim();
  if (typeof source === 'object' && source !== null) {
    const s = source as { provider?: unknown; source?: unknown; name?: unknown };
    const provider = typeof s.provider === 'string' ? s.provider.trim() : '';
    const sourceName = typeof s.source === 'string' ? s.source.trim() : '';
    if (provider) return provider;
    if (sourceName) return sourceName;
  }
  return null;
}

function getSleepSessionTime(record: SleepRecord, keys: string[]): string {
  const value = getValue(record, keys);
  if (value === undefined || value === null || value === '') {
    return '—';
  }

  const parsed = moment(String(value));
  if (!parsed.isValid()) {
    return '—';
  }

  return parsed.format('h:mm A');
}

function formatDurationMinutes(totalMinutes: number): string {
  if (!Number.isFinite(totalMinutes) || totalMinutes <= 0) return '0h 0m';

  const hours = Math.floor(totalMinutes / 60);
  const minutes = Math.round(totalMinutes % 60);

  if (hours > 0 && minutes > 0) {
    return `${hours}h ${minutes}m`;
  }

  if (hours > 0) {
    return `${hours}h`;
  }

  return `${minutes}m`;
}

function formatBedtimeMinutes(minutes: number | null): string {
  if (minutes === null || !Number.isFinite(minutes)) return '—';

  const normalizedMinutes = minutes >= 24 * 60 ? minutes - 24 * 60 : minutes;
  const totalMinutes = Math.round(normalizedMinutes);
  const hours = Math.floor(totalMinutes / 60) % 24;
  const mins = totalMinutes % 60;
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;

  return `${displayHours}:${mins.toString().padStart(2, '0')} ${period}`;
}

function getBedtimeMinutes(record: SleepRecord): number | null {
  const bedtimeValue = getValue(record, ['start_time', 'bedtime', 'sleep_start', 'bedtime_start', 'start', 'sleep_time']);
  if (bedtimeValue === undefined || bedtimeValue === null || bedtimeValue === '') return null;

  const bedtimeMoment = moment(String(bedtimeValue));
  if (!bedtimeMoment.isValid()) return null;

  let minutes = bedtimeMoment.hours() * 60 + bedtimeMoment.minutes();
  if (minutes < 6 * 60) {
    minutes += 24 * 60;
  }

  return minutes;
}

export function calculateSleepSummary(records: SleepRecord[]): SleepSummaryTotals {
  const nightsTracked = records.length;

  const totals = records.reduce<{
    totalSleepMinutes: number;
    totalEfficiency: number;
    deepMinutes: number;
    remMinutes: number;
    lightMinutes: number;
    awakeMinutes: number;
  }>(
    (acc, record) => {
      const sleepMinutes =
        toNumber(getValue(record, ['total_sleep_duration_minutes', 'sleep_duration_minutes', 'duration_minutes'])) ??
        (toNumber(getValue(record, ['sleep_duration_seconds', 'duration_seconds'])) ?? 0) / 60;

      const efficiency =
        toNumber(getValue(record, ['efficiency_percent', 'sleep_efficiency'])) ??
        toNumber(getValue(record, ['efficiency'])) ?? 0;

      const stages = (getValue(record, ['stages']) as Record<string, unknown> | undefined) ?? {};
      const deep = toNumber(getValue(stages, ['deep_minutes'])) ?? toNumber(getValue(record, ['deep_minutes'])) ?? 0;
      const rem = toNumber(getValue(stages, ['rem_minutes'])) ?? toNumber(getValue(record, ['rem_minutes'])) ?? 0;
      const light = toNumber(getValue(stages, ['light_minutes'])) ?? toNumber(getValue(record, ['light_minutes'])) ?? 0;
      const awake = toNumber(getValue(stages, ['awake_minutes'])) ?? toNumber(getValue(record, ['awake_minutes'])) ?? 0;

      acc.totalSleepMinutes += sleepMinutes;
      acc.totalEfficiency += efficiency;
      acc.deepMinutes += deep;
      acc.remMinutes += rem;
      acc.lightMinutes += light;
      acc.awakeMinutes += awake;

      return acc;
    },
    {
      totalSleepMinutes: 0,
      totalEfficiency: 0,
      deepMinutes: 0,
      remMinutes: 0,
      lightMinutes: 0,
      awakeMinutes: 0
    }
  );

  const bedtimeMinutes = records
    .map(getBedtimeMinutes)
    .filter((value): value is number => value !== null);

  const avgBedtimeMinutes = bedtimeMinutes.length > 0 ? bedtimeMinutes.reduce((sum, value) => sum + value, 0) / bedtimeMinutes.length : null;

  return {
    nightsTracked,
    totalSleepMinutes: totals.totalSleepMinutes,
    avgEfficiency: nightsTracked > 0 ? totals.totalEfficiency / nightsTracked : 0,
    avgDurationMinutes: nightsTracked > 0 ? totals.totalSleepMinutes / nightsTracked : 0,
    avgBedtimeMinutes,
    avgBedtime: avgBedtimeMinutes === null ? '—' : formatBedtimeMinutes(avgBedtimeMinutes),
    deepMinutes: totals.deepMinutes,
    remMinutes: totals.remMinutes,
    lightMinutes: totals.lightMinutes,
    awakeMinutes: totals.awakeMinutes
  };
}

function useSleepSummaryData(userId: string, startOfRange: moment.Moment, endOfRange: moment.Moment) {
  const [records, setRecords] = useState<SleepRecord[]>([]);
  const [isFetching, setIsFetching] = useState(true);

  useEffect(() => {
    let isActive = true;

    const loadRecords = async () => {
      setIsFetching(true);

      const collected: SleepRecord[] = [];
      let nextCursor: string | undefined = undefined;
      let hasMore = true;

      while (hasMore) {
        const result: { data?: unknown } = await store.dispatch(
          apiSlice.endpoints.getUserSleepSummary.initiate({
            userId,
            from: startOfRange.toISOString(),
            to: endOfRange.toISOString(),
            limit: 100,
            cursor: nextCursor
          })
        );

        if ('data' in result) {
          const { items: pageData, pagination } = normalizeCursorPage<SleepRecord>(result.data);
          collected.push(...pageData);

          const nextPageCursor = pagination?.next_cursor ?? undefined;
          hasMore = Boolean(pagination?.has_more) && Boolean(nextPageCursor);
          nextCursor = nextPageCursor;

          if (!pagination?.has_more || !nextPageCursor) {
            hasMore = false;
          }
        } else {
          hasMore = false;
        }

        const unsubscribe = (result as unknown as { unsubscribe?: () => void }).unsubscribe;
        if (typeof unsubscribe === 'function') {
          unsubscribe();
        }
      }

      if (isActive) {
        setRecords(collected);
        setIsFetching(false);
      }
    };

    void loadRecords();

    return () => {
      isActive = false;
    };
  }, [userId, startOfRange, endOfRange]);

  return { records, isFetching };
}

export default function SleepTab({ userId }: SleepTabProps) {
  const { t } = useTranslation();
  const [selectedRange, setSelectedRange] = useState<SleepDayRange>(30);
  const [selectedMetric, setSelectedMetric] = useState<SleepMetricKey>('avgEfficiency');
  const { start: startOfRange, end: endOfRange } = useMemo(
    () => getSleepDateRange(selectedRange),
    [selectedRange]
  );

  const { records, isFetching } = useSleepSummaryData(userId, startOfRange, endOfRange);

  const filteredRecords: SleepRecord[] = useMemo(() => {
    return records.filter((record) => {
      const dateValue = getValue(record, ['date']);
      if (!dateValue) return false;

      const date = moment(String(dateValue));
      return date.isValid() && date.isBetween(startOfRange, endOfRange, 'day', '[]');
    });
  }, [records, startOfRange, endOfRange]);

  const [deduplicate, setDeduplicate] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageCache, setPageCache] = useState<Record<number, { items: SleepRecord[]; nextCursor: string | null }>>({});
  const [pageLoading, setPageLoading] = useState(false);

  useEffect(() => {
    setCurrentPage(1);
    setPageCache({});
    setPageLoading(false);
  }, [userId, deduplicate]);

  useEffect(() => {
    const page = currentPage;

    if (pageCache[page]) {
      setPageLoading(false);
      return;
    }

    let isActive = true;
    const cursor = page === 1 ? undefined : pageCache[page - 1]?.nextCursor ?? undefined;

    if (page > 1 && !cursor) {
      return;
    }

    const loadPage = async () => {
      setPageLoading(true);

      const result = await store.dispatch(
        apiSlice.endpoints.getUserSleep.initiate({
          userId,
          from: moment(0).toISOString(),
          to: moment().endOf('day').toISOString(),
          limit: 10,
          cursor,
          filterByPriority: deduplicate
        })
      );

      if ('data' in result) {
        const { items, pagination } = normalizeCursorPage<SleepRecord>(result.data);
        const nextCursor = pagination?.next_cursor ?? null;

        if (isActive) {
          setPageCache((prev) => ({ ...prev, [page]: { items, nextCursor } }));
        }
      }

      if (isActive) {
        setPageLoading(false);
      }

      const unsubscribe = (result as unknown as { unsubscribe?: () => void }).unsubscribe;
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };

    void loadPage();

    return () => {
      isActive = false;
    };
  }, [currentPage, userId, deduplicate, pageCache]);

  const currentPageData = pageCache[currentPage] ?? { items: [], nextCursor: null };
  const hasNextPage = Boolean(currentPageData.nextCursor);

  const filteredSessions = useMemo(
    () => currentPageData.items,
    [currentPageData.items]
  );

  const summary = useMemo(() => calculateSleepSummary(filteredRecords), [filteredRecords]);
  const stageBreakdown = useMemo(() => {
    const nightCount = Math.max(summary.nightsTracked, 1);
    const stages = [
      { key: 'deep', label: 'Deep', minutes: summary.deepMinutes / nightCount, color: '#6366f1' },
      { key: 'rem', label: 'REM', minutes: summary.remMinutes / nightCount, color: '#e45cf6' },
      { key: 'light', label: 'Light', minutes: summary.lightMinutes / nightCount, color: '#38bdf8' },
      { key: 'awake', label: 'Awake', minutes: summary.awakeMinutes / nightCount, color: 'grey.300' }
    ];

    const total = stages.reduce((acc, stage) => acc + stage.minutes, 0) || 1;

    return stages.map((stage) => ({
      ...stage,
      percent: (stage.minutes / total) * 100
    }));
  }, [summary]);

  type StageBreakdownItem = {
    key: string;
    label: string;
    minutes: number;
    color: string;
    percent: number;
  };

  function SleepStagesBar({ stages, height = 10 }: { stages: StageBreakdownItem[]; height?: number }) {
    return (
      <Box
        sx={{
          height,
          display: 'flex',
          overflow: 'hidden',
          borderRadius: 999,
          bgcolor: 'action.hover'
        }}
      >
        {stages.map(
          (stage) =>
            stage.percent > 0 && (
              <Box
                key={stage.key}
                sx={{
                  width: `${stage.percent}%`,
                  bgcolor: stage.color,
                  transition: 'width 0.2s ease'
                }}
              />
            )
        )}
      </Box>
    );
  }

  function SleepStageLegend({ stages }: { stages: StageBreakdownItem[] }) {
    return (
      <Grid container sx={{ mt: 1 }}>
        {stages.map((stage) => (
          <Grid item xs={6} md={3} key={stage.key}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: stage.color }} />
              <Typography variant="caption" color="text.secondary">
                {stage.label}
              </Typography>
              <Typography variant="caption" sx={{ ml: 'auto', fontWeight: 600 }}>
                {Math.round(stage.percent)}%
              </Typography>
            </Stack>
          </Grid>
        ))}
      </Grid>
    );
  }

  const metricCards = useMemo(
    () => [
      {
        key: 'nightsTracked',
        label: t('nights_tracked', 'Nights Tracked'),
        accent: '#8b5cf6',
        bgColor: 'rgba(139, 92, 246, 0.12)',
        icon: BedDouble,
        value: summary.nightsTracked,
        getChartValue: () => 1,
        chartLabel: 'Nights Tracked',
        isStatic: true
      },
      {
        key: 'avgEfficiency',
        label: t('avg_efficiency', 'Average Efficiency'),
        accent: '#22c55e',
        bgColor: 'rgba(34, 197, 94, 0.12)',
        icon: TrendingUp,
        value: `${Math.round(summary.avgEfficiency)}%`,
        getChartValue: (record: SleepRecord) => toNumber(getValue(record, ['efficiency_percent', 'sleep_efficiency', 'efficiency'])) ?? 0,
        chartLabel: 'Average Efficiency',
        isStatic: false
      },
      {
        key: 'avgDuration',
        label: t('avg_duration', 'Average Duration'),
        accent: '#a78bfa',
        bgColor: 'rgba(167, 139, 250, 0.12)',
        icon: Moon,
        value: formatDurationMinutes(summary.avgDurationMinutes),
        getChartValue: (record: SleepRecord) =>
          toNumber(getValue(record, ['total_sleep_duration_minutes', 'sleep_duration_minutes', 'duration_minutes'])) ??
          (toNumber(getValue(record, ['sleep_duration_seconds', 'duration_seconds'])) ?? 0) / 60,
        chartLabel: 'Average Duration',
        isStatic: false
      },
      {
        key: 'avgBedtime',
        label: t('avg_bedtime', 'Average Bedtime'),
        accent: '#38bdf8',
        bgColor: 'rgba(56, 189, 248, 0.12)',
        icon: Clock3,
        value: summary.avgBedtime,
        getChartValue: (record: SleepRecord) => {
          const bedtime = getBedtimeMinutes(record);
          return bedtime ?? 0;
        },
        chartLabel: 'Average Bedtime',
        isStatic: false
      }
    ] as const,
    [summary, t]
  );

  const currentMetric = metricCards.find((metric) => metric.key === selectedMetric) ?? metricCards[1];

  const chartTitle =
    selectedMetric === 'avgEfficiency'
      ? t('daily_efficiency', 'Daily Average Efficiency')
      : selectedMetric === 'avgDuration'
        ? t('daily_duration', 'Daily Average Duration')
        : t('daily_bedtime', 'Daily Average Bedtime');

  const chartData = useMemo(() => {
    return [...filteredRecords]
      .sort((a, b) => {
        const dateA = moment(String(getValue(a, ['date'])));
        const dateB = moment(String(getValue(b, ['date'])));
        return dateA.valueOf() - dateB.valueOf();
      })
      .map((record) => {
        const dateValue = getValue(record, ['date']);
        const dateLabel = dateValue ? moment(String(dateValue)).locale(i18n.language).format('MMM D') : '—';
        const numericValue = currentMetric.getChartValue(record);

        return {
          label: dateLabel,
          value: numericValue,
          displayValue:
            currentMetric.key === 'avgBedtime'
              ? formatBedtimeMinutes(numericValue)
              : currentMetric.key === 'avgDuration'
                ? formatDurationMinutes(numericValue)
                : currentMetric.key === 'nightsTracked'
                  ? String(Math.round(numericValue))
                  : `${Math.round(numericValue)}%`
        };
      });
  }, [filteredRecords, currentMetric]);

  const chartOptions = useMemo(
    () => ({
      chart: {
        type: 'bar' as const,
        toolbar: { show: false },
        background: 'transparent'
      },
      colors: [currentMetric.accent],
      plotOptions: {
        bar: {
          borderRadius: 6,
          borderRadiusApplication: 'end' as const
        }
      },
      dataLabels: { enabled: false },
      grid: {
        borderColor: 'rgba(148, 163, 184, 0.15)',
        strokeDashArray: 3
      },
      tooltip: {
        theme: 'dark',
        y: {
          formatter: (value: number) => formatSleepTooltipValue(value, currentMetric.key as SleepMetricKey)
        }
      },
      xaxis: {
        categories: chartData.map((item) => item.label),
        labels: {
          style: {
            colors: '#94a3b8',
            fontSize: '11px'
          }
        }
      },
      yaxis: {
        labels: {
          formatter: (value: number) => formatSleepAxisValue(value, currentMetric.key as SleepMetricKey),
          style: {
            colors: '#94a3b8',
            fontSize: '11px'
          }
        }
      },
      stroke: { show: false }
    } satisfies ApexOptions),
    [chartData, currentMetric]
  );

  return (
    <Stack spacing={2}>
      <MainCard
        title={t('summary', 'Summary')}
        secondary={
          <PatientDateRangeSelector value={selectedRange} onChange={setSelectedRange} />
        }
      >
        {isFetching ? (
          <Grid container spacing={2}>
            {[1, 2, 3, 4].map((item) => (
              <Grid item xs={12} md={3} key={item}>
                <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                  <Stack spacing={1.5}>
                    <Box sx={{ p: 1, width: 36, height: 36, minWidth: 36, borderRadius: 2, bgcolor: 'action.hover' }} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      <Box sx={{ width: '42%', height: 28, borderRadius: 1, bgcolor: 'action.hover' }} />
                      <Box sx={{ width: '60%', height: 14, borderRadius: 1, bgcolor: 'action.hover' }} />
                    </Box>
                  </Stack>
                </Box>
              </Grid>
            ))}
          </Grid>
        ) : (
          <>
            <Grid container spacing={2}>
              {metricCards.map((metric) => {
                const isSelected = !metric.isStatic && selectedMetric === metric.key;

                return (
                  <Grid item xs={12} md={3} key={metric.key}>
                    <PatientMetricCard
                      icon={metric.icon}
                      accent={metric.accent}
                      bgColor={metric.bgColor}
                      value={metric.value}
                      label={metric.label}
                      selected={isSelected}
                      selectable={!metric.isStatic}
                      onSelect={() => {
                        if (metric.key !== 'nightsTracked') {
                          setSelectedMetric(metric.key);
                        }
                      }}
                    />
                  </Grid>
                );
              })}
            </Grid>

            {chartData.length > 1 && (
              <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                  {chartTitle}
                </Typography>
                <ApexChart
                  options={chartOptions}
                  series={[{ name: currentMetric.chartLabel, data: chartData.map((item) => item.value) }]}
                  type="bar"
                  height={220}
                />
              </Box>
            )}

            <Box
              sx={{
                mt: 3,
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                bgcolor: 'background.paper'
              }}
            >
              <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Average Sleep Stages
              </Typography>

              <SleepStagesBar stages={stageBreakdown} height={12} />
              <SleepStageLegend stages={stageBreakdown} />
            </Box>
          </>
        )}
      </MainCard>

      <MainCard
        title={t('sleep_sessions', 'Sleep Sessions')}
        secondary={
          <Stack direction="row" spacing={1} alignItems="center">
            <FormControlLabel
              control={
                <Switch
                  checked={deduplicate}
                  onChange={() => setDeduplicate((prev) => !prev)}
                  inputProps={{ 'aria-label': 'Deduplicate sources' }}
                />
              }
              label={<Typography variant="caption" color="text.secondary">Deduplicate sources</Typography>}
              sx={{ mr: 0, ml: 0 }}
            />
            <Typography variant="caption" color="text.secondary">
              {t('page', 'Page')} {currentPage}
            </Typography>
          </Stack>
        }
      >
        {pageLoading ? (
          <Stack spacing={1.5}>
            {[1, 2, 3, 4].map((item) => (
              <Box
                key={item}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  overflow: 'hidden',
                  bgcolor: 'background.paper'
                }}
              >
                <Box sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0, gap: 2 }}>
                    <Box sx={{ width: 132, flexShrink: 0 }}>
                      <Box sx={{ width: 112, height: 16, borderRadius: 1, bgcolor: 'action.hover', mb: 0.75 }} />
                      <Box sx={{ width: 72, height: 12, borderRadius: 1, bgcolor: 'action.hover' }} />
                      <Box sx={{ mt: 1, width: 76, height: 28, borderRadius: 999, bgcolor: 'action.hover' }} />
                    </Box>

                    <Grid container spacing={2} sx={{ flex: 1, minWidth: 0, alignItems: 'center' }}>
                      {[1, 2, 3, 4, 5].map((metricItem) => (
                        <Grid item xs={6} md={2.4} key={metricItem}>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ justifyContent: 'center' }}>
                            <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: 'action.hover', flexShrink: 0 }} />
                            <Box>
                              <Box sx={{ width: 56, height: 14, borderRadius: 1, bgcolor: 'action.hover', mb: 0.5 }} />
                              <Box sx={{ width: 40, height: 12, borderRadius: 1, bgcolor: 'action.hover' }} />
                            </Box>
                          </Stack>
                        </Grid>
                      ))}

                      <Grid item xs={12}>
                        <Stack spacing={1} sx={{ pt: 0.5 }}>
                          <Box sx={{ width: '100%', height: 10, borderRadius: 999, bgcolor: 'action.hover' }} />
                          <Grid container sx={{ mt: 1 }}>
                            {[1, 2, 3, 4].map((legendItem) => (
                              <Grid item xs={6} md={3} key={legendItem}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'action.hover' }} />
                                  <Box sx={{ width: 44, height: 12, borderRadius: 1, bgcolor: 'action.hover' }} />
                                  <Box sx={{ ml: 'auto', width: 20, height: 12, borderRadius: 1, bgcolor: 'action.hover' }} />
                                </Stack>
                              </Grid>
                            ))}
                          </Grid>
                        </Stack>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              </Box>
            ))}
          </Stack>
        ) : filteredSessions.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No sleep sessions available.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {filteredSessions.map((session) => {
              const dateValue = getValue(session, ['date', 'end_time', 'start_time']);
              const sessionDate = dateValue ? moment(String(dateValue)) : null;
              const startTime = getSleepSessionTime(session, ['start_time', 'bedtime', 'sleep_start', 'bedtime_start', 'start', 'sleep_time']);
              const wakeTime = getSleepSessionTime(session, ['wake_time', 'end_time', 'sleep_end', 'end']);
              const durationMinutes = toNumber(getValue(session, ['sleep_duration_minutes', 'total_sleep_duration_minutes', 'duration_minutes'])) ??
                (toNumber(getValue(session, ['sleep_duration_seconds', 'duration_seconds'])) ?? 0) / 60;
              const timeInBedMinutes = toNumber(getValue(session, ['duration_minutes', 'time_in_bed_minutes'])) ??
                (toNumber(getValue(session, ['duration_seconds', 'time_in_bed_seconds'])) ?? 0) / 60;
              const efficiency = toNumber(getValue(session, ['efficiency_percent', 'sleep_efficiency', 'efficiency'])) ?? 0;
              const stageBreakdown = getSessionStageBreakdown(session);
              const source = getRecordSource(session);

              return (
                <Box key={String(getValue(session, ['id']) ?? `${sessionDate?.format('YYYY-MM-DD') ?? 'session'}-${Math.random()}`)} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', bgcolor: 'background.paper' }}>
                  <Box sx={{ p: 2 }}>
                    <Stack spacing={1.5}>
                      <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 2, px: 0, py: 0, textAlign: 'left' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0, gap: 2 }}>
                          <Box sx={{ width: 132, flexShrink: 0 }}>
                            <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                              {sessionDate?.isValid() ? sessionDate.locale(i18n.language).format('ddd, MMM D') : '—'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {sessionDate?.isValid() ? sessionDate.locale(i18n.language).format('YYYY') : '—'}
                            </Typography>

                            {source && (
                              <Box sx={{ pt: 1 }}>
                                <ProviderChip provider={source} />
                              </Box>
                            )}
                          </Box>

                          <Grid container spacing={2} sx={{ flex: 1, minWidth: 0, alignItems: 'center' }}>
                            <Grid item xs={6} md={2.4}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <TrendingUp size={14} style={{ color: '#22c55e', flexShrink: 0 }} />
                                <Box>
                                  <Typography variant="caption" color="text.secondary">Efficiency</Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{Math.round(efficiency)}%</Typography>
                                </Box>
                              </Stack>
                            </Grid>
                            <Grid item xs={6} md={2.4}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Moon size={14} style={{ color: '#a78bfa', flexShrink: 0 }} />
                                <Box>
                                  <Typography variant="caption" color="text.secondary">Duration</Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatDurationMinutes(durationMinutes)}</Typography>
                                </Box>
                              </Stack>
                            </Grid>
                            <Grid item xs={6} md={2.4}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <BedDouble size={14} style={{ color: '#8b5cf6', flexShrink: 0 }} />
                                <Box>
                                  <Typography variant="caption" color="text.secondary">Time in Bed</Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatDurationMinutes(timeInBedMinutes)}</Typography>
                                </Box>
                              </Stack>
                            </Grid>
                            <Grid item xs={6} md={2.4}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Clock3 size={14} style={{ color: '#38bdf8', flexShrink: 0 }} />
                                <Box>
                                  <Typography variant="caption" color="text.secondary">Bedtime</Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{startTime}</Typography>
                                </Box>
                              </Stack>
                            </Grid>
                            <Grid item xs={6} md={2.4}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Clock3 size={14} style={{ color: '#f59e0b', flexShrink: 0 }} />
                                <Box>
                                  <Typography variant="caption" color="text.secondary">Wake</Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{wakeTime}</Typography>
                                </Box>
                              </Stack>
                            </Grid>

                            <Grid item xs={12}>
                              <Stack spacing={1} sx={{ pt: 0.5 }}>
                                <SleepStagesBar stages={stageBreakdown} height={10} />
                                <SleepStageLegend stages={stageBreakdown} />
                              </Stack>
                            </Grid>
                          </Grid>
                        </Box>
                      </Box>

                    </Stack>
                  </Box>
                </Box>
              );
            })}

            <Stack spacing={2} sx={{ pt: 1.5 }} alignItems="flex-end">
              <Pagination
                count={Math.max(1, currentPage + (hasNextPage ? 1 : 0))}
                size="medium"
                page={currentPage}
                variant="combined"
                color="primary"
                onChange={(_, page) => {
                  if (!pageLoading && page !== currentPage) {
                    setCurrentPage(page);
                  }
                }}
              />
            </Stack>
          </Stack>
        )}
      </MainCard>
    </Stack>
  );
}
